import { CoTeacherRole } from '../../BackendInterface';

/**
 * Display names for the administrator roles. Exported because surfaces that
 * list co-teachers as plain text rather than chips need the same wording -
 * see MyCoTeacherMembershipsPage.
 */
export const CO_TEACHER_ROLE_LABELS: Record<CoTeacherRole, string> = {
  Teacher: 'Teacher',
  SchoolAdmin: 'School Admin',
  DistrictAdmin: 'District Admin',
};

/**
 * The wording for a role, or null when there is nothing worth saying: plain
 * teachers are the unremarkable case, a missing role means an older cached
 * response, and an unrecognised role means the backend grew one this build
 * does not know about. All three render nothing rather than an empty chip.
 */
export function getCoTeacherRoleLabel(role?: CoTeacherRole): string | null {
  if (!role || role === 'Teacher') {
    return null;
  }

  return CO_TEACHER_ROLE_LABELS[role] ?? null;
}

interface Props {
  role?: CoTeacherRole;
}

/**
 * Names the role of an administrator listed among a classroom's teachers, so
 * nobody adds or removes one without realising it is an administrator account
 * (PE-1818).
 */
const CoTeacherRoleBadge = ({ role }: Props): JSX.Element | null => {
  const label = getCoTeacherRoleLabel(role);
  if (!label) {
    return null;
  }

  return (
    // Matches the sibling "Pending" chip in ManageCoTeachersModal so the two
    // read as one badge language.
    <span className='ml-[0.6rem] shrink-0 rounded bg-slate-200 px-[0.5rem] py-[0.2rem] text-[1.2rem] text-slate-700'>
      {label}
    </span>
  );
};

export default CoTeacherRoleBadge;
